/**
 * Desktop Discover gallery tile (lane D2). INVENTORY-DESKTOP §3.3, §6.3, §6.4;
 * `design/Games Tonight.dc.html` lines 141-156.
 *
 * WHICH SPEC WINS (§6.3, settled). `design/Result Tile.dc.html` is a standalone
 * component the app template never imports. The APP's inlined tile is
 * authoritative for structure, ribbons, the fallback hue and the review copy —
 * all of which already exist as `ribbonOf()` / `fallGradient()` / `revMeta()`
 * inside `tileVM`. `Result Tile.dc.html` is authoritative ONLY for the tilt and
 * sheen maths, and D0 already lifted that into `useHoverTilt`. So this file is
 * a port of the app template, not of the `.dc.html` component.
 *
 * WHY THE HOOK AND NOT `<HoverTile>`. The prototype puts the sheen overlay
 * BETWEEN the scrim and the caption (line 146 vs 147), so the `mix-blend-mode:
 * screen` layer never washes over the title, developer and price text.
 * `HoverTile` appends the sheen as the LAST child, which would put it on top of
 * the caption. D0's own rule covers this case: use the hook wherever the sheen
 * is not the tilted element's last child.
 *
 * DESKTOP-ONLY vs the mobile tile (`screens/Discover.tsx`):
 *   - the hover tilt itself, and an ANIMATED sheen instead of the fixed
 *     `TILE_SHEEN` constant `tileVM.sheen` still carries (mobile keeps using it);
 *   - the add button is 20×20 (mobile 38×38); both carry `TileVM.addTip` and
 *     open the ADD IT chooser (docs/ADD-IT.md);
 *   - name 12.5px and the review line 9px (mobile rounded both down a notch to
 *     fit the 430px column).
 *
 * The CheapShark quick-add gate is NOT a desktop deviation — it is the app's own
 * PORTING-NOTES D-02 behaviour, shared with mobile: a `cs…` row has no appid the
 * `list_entries` FK can hold, so its ✚ is inert and the tooltip explains why.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { Ribbon } from '../../components/Ribbon.tsx';
import { useArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { NO_STEAM_TIP, quickAddBlocked } from '../../store/selectors.3c.ts';
import type { discoverVM } from '../../store/selectors.ts';
import { useHoverTilt } from '../useHoverTilt.ts';

export type ResultVM = ReturnType<typeof discoverVM>['results'][number];

/** The ✚ tone when the row cannot be added at all (shared with the list row). */
export const ADD_BLOCKED = {
  bd: 'var(--gt-border-2)',
  clr: 'var(--gt-muted-2)',
  bg: 'rgba(5,5,8,.5)',
} as const;

export function ResultTile({ g }: { g: ResultVM }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const openAdd = useQd((s) => s.openAdd);
  const art = useArt(g.id + 'p', g.coverCands);
  const tilt = useHoverTilt(g.id);

  const blocked = quickAddBlocked(g.id) && !g.inList;
  const tone = blocked ? ADD_BLOCKED : { bd: g.addBd, clr: g.addClr, bg: g.addBg };

  return (
    <div
      ref={tilt.ref}
      {...tilt.handlers}
      onClick={() => openGame(g.id)}
      style={{
        position: 'relative',
        aspectRatio: '2/3',
        background: g.fall,
        overflow: 'hidden',
        border: '1px solid var(--gt-border)',
        cursor: 'pointer',
        ...tilt.rest,
      }}
    >
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      <Ribbon up={g.upcoming} ea={g.ea} demo={g.demo} />
      {g.hasDisc ? (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontFamily: 'var(--gt-font-mono)',
            fontWeight: 700,
            fontSize: 11,
            background: 'var(--acc)',
            color: 'var(--gt-ink)',
            padding: '3px 6px',
          }}
        >
          {g.disc}
        </div>
      ) : null}
      <div
        style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 38%,rgba(5,5,8,.97) 78%)' }}
      />
      {/* the animated sheen — under the caption, exactly where the prototype
          puts it (line 146), which is why this is the hook and not HoverTile */}
      <div ref={tilt.sheenRef} style={tilt.restSheen} />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '11px 11px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.2, color: '#fff' }}>{g.name}</div>
        <div style={{ fontSize: 10.5, color: 'var(--gt-text-4)' }}>{g.dev}</div>
        <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 9, color: 'var(--gt-text-5)', letterSpacing: '.03em' }}>
          {g.priceTxt} · {g.playersTxt} · {g.dateTxt}
        </div>
        {/* Why the feed chose this row — server-authored, rendered verbatim,
            empty on every non-feed row. See the mobile screen's note. */}
        {g.hasWhy ? (
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 9,
              color: 'var(--acc)',
              letterSpacing: '.04em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {g.whyTxt}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 1 }}>
          <span
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 9,
              color: g.revClr,
              whiteSpace: 'nowrap',
              display: 'flex',
              gap: 4,
              minWidth: 0,
            }}
          >
            {/* The label truncates; the number never does — see `TileVM.revLbl`. */}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
              ▮ {g.revLbl}
            </span>
            {g.revPct ? <span style={{ flex: 'none' }}>{g.revPct}</span> : null}
          </span>
          <span
            data-tip={blocked ? NO_STEAM_TIP : g.addTip}
            onClick={(ev) => {
              ev.stopPropagation();
              if (blocked) return;
              openAdd(g.id);
            }}
            style={{
              width: 20,
              height: 20,
              border: '1px solid ' + tone.bd,
              color: tone.clr,
              background: tone.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBottom: 1.5,
              boxSizing: 'border-box',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1,
              flex: 'none',
              cursor: blocked ? 'not-allowed' : 'pointer',
            }}
          >
            {g.addG}
          </span>
        </div>
      </div>
    </div>
  );
}

export default ResultTile;
